import { useState, useMemo } from 'react';
import { useCostTracking } from '../../contexts/CostTrackingContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import type { ExpenseFilterCriteria } from '../../types/costTrackingTypes';
import { Btn, DateField, Select } from '../ui';
import '../../css/CostTrackingDashboard.css';

interface ExpenseFilterBarProps {
    activeCategory: string | null;
    onCategoryChange: (category: string | null) => void;
    onFilterChange: (criteria: ExpenseFilterCriteria) => void;
}

/**
 * Filter bar with an All Costs pill, a Category dropdown, and a Filters button.
 * Filters opens Date Range, Equipment, Vendor, and Expense Category.
 */
export const ExpenseFilterBar = ({
    activeCategory,
    onCategoryChange,
    onFilterChange,
}: ExpenseFilterBarProps) => {
    const { expenseCategories, expenseRecords } = useCostTracking();
    const { companyGroupedEquipmentData } = useEquipmentContext();

    // Build unit name lookup: unitId -> "Category -- Unit Name"
    const unitNameMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const group of (companyGroupedEquipmentData || [])) {
            const catName = group.name || '';
            for (const unit of (group.units || [])) {
                map.set(unit.id, catName ? `${catName} -- ${unit.name}` : unit.name);
            }
        }
        return map;
    }, [companyGroupedEquipmentData]);

    const [showAdvanced, setShowAdvanced] = useState(false);
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [equipmentId, setEquipmentId] = useState('');
    const [vendorId, setVendorId] = useState('');
    const [filterCategory, setFilterCategory] = useState('');

    // Build unique equipment IDs with resolved names from expense records
    const uniqueEquipment = useMemo(() => {
        const map = new Map<string, string>();
        expenseRecords.forEach((r) => {
            if (r.unitId && !map.has(r.unitId)) {
                map.set(r.unitId, unitNameMap.get(r.unitId) || r.unitId);
            }
        });
        return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    }, [expenseRecords, unitNameMap]);

    const uniqueVendors = useMemo(() => {
        const map = new Map<string, string>();
        expenseRecords.forEach((r) => {
            if (r.vendorId && r.vendorName) map.set(r.vendorId, r.vendorName);
        });
        return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    }, [expenseRecords]);

    const handleApplyFilters = () => {
        const criteria: ExpenseFilterCriteria = {};
        if (dateStart && dateEnd) {
            // Set start to beginning of day and end to end of day to include full days
            const start = new Date(dateStart + 'T00:00:00');
            const end = new Date(dateEnd + 'T23:59:59');
            criteria.dateRange = { start, end };
        } else if (dateStart) {
            criteria.dateRange = { start: new Date(dateStart + 'T00:00:00'), end: new Date('2099-12-31') };
        } else if (dateEnd) {
            criteria.dateRange = { start: new Date('2000-01-01'), end: new Date(dateEnd + 'T23:59:59') };
        }
        if (equipmentId) criteria.equipmentId = equipmentId;
        if (vendorId) criteria.vendorId = vendorId;
        if (filterCategory) criteria.categoryName = filterCategory;
        onFilterChange(criteria);
    };

    const handleClearFilters = () => {
        setDateStart('');
        setDateEnd('');
        setEquipmentId('');
        setVendorId('');
        setFilterCategory('');
        onFilterChange({});
    };

    return (
        <div className="ct-filter-bar">
            {/* Category tabs */}
            <div className="ct-filter-tabs">
                <button
                    type="button"
                    className={`ct-filter-tab ${activeCategory === null ? 'active' : ''}`}
                    onClick={() => onCategoryChange(null)}
                >
                    All Costs
                </button>
                <Select
                    className="ct-category-select"
                    ariaLabel="Filter by category"
                    placeholder="Category"
                    value={activeCategory ?? ''}
                    onChange={(value) => onCategoryChange(value || null)}
                    options={expenseCategories.map((cat) => ({
                        value: cat.name,
                        label: cat.name,
                    }))}
                />

                <Btn
                    variant="dotted"
                    buttonShape="pill"
                    buttonClasses="ct-filter-btn"
                    ariaLabel="Filters"
                    buttonAriaProps={{ 'aria-expanded': showAdvanced, 'aria-pressed': showAdvanced }}
                    buttonOnClick={() => setShowAdvanced((prev) => !prev)}
                    buttonIcon={(
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                            <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                            <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                            <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
                            <line x1="17" y1="16" x2="23" y2="16" />
                        </svg>
                    )}
                    buttonText={<span className="ct-filter-btn-label">Filters</span>}
                />
            </div>

            {/* Advanced filter panel */}
            {showAdvanced && (
                <div className="ct-advanced-filters">
                    <div className="ct-filter-group">
                        <label className="ct-filter-label">Date Range</label>
                        <div className="ct-filter-date-range">
                            <DateField
                                value={dateStart}
                                onChange={setDateStart}
                                placeholder="Start date"
                                ariaLabel="Start date"
                            />
                            <span className="ct-filter-date-sep">to</span>
                            <DateField
                                value={dateEnd}
                                onChange={setDateEnd}
                                placeholder="End date"
                                ariaLabel="End date"
                            />
                        </div>
                    </div>

                    <div className="ct-filter-group">
                        <label className="ct-filter-label">Equipment</label>
                        <Select
                            ariaLabel="Filter by equipment"
                            placeholder="All Equipment"
                            value={equipmentId}
                            onChange={setEquipmentId}
                            options={uniqueEquipment.map(([id, label]) => ({ value: id, label }))}
                        />
                    </div>

                    <div className="ct-filter-group">
                        <label className="ct-filter-label">Vendor</label>
                        <Select
                            ariaLabel="Filter by vendor"
                            placeholder="All Vendors"
                            value={vendorId}
                            onChange={setVendorId}
                            options={uniqueVendors.map(([id, name]) => ({ value: id, label: name }))}
                        />
                    </div>

                    <div className="ct-filter-group">
                        <label className="ct-filter-label">Expense Category</label>
                        <Select
                            ariaLabel="Filter by expense category"
                            placeholder="All Categories"
                            value={filterCategory}
                            onChange={setFilterCategory}
                            options={expenseCategories.map((cat) => ({
                                value: cat.name,
                                label: cat.name,
                            }))}
                        />
                    </div>

                    <div className="ct-filter-actions">
                        <Btn variant="primary" buttonSize="sm" buttonText="Apply Filters" buttonOnClick={handleApplyFilters} />
                        <Btn variant="secondary-outlined" buttonSize="sm" buttonText="Clear" buttonOnClick={handleClearFilters} />
                    </div>
                </div>
            )}
        </div>
    );
};
